import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { IconButton } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, Fragment, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { agencyScheduleDescription } from "../../utils/agencyPresentation";
import { hasLegacyAgencyPosting } from "../../utils/agencyTargets";
import type { OpenShiftRequest, ShiftRequest } from "../../utils/models";
import { InHouseTooltip } from "../InHouseTooltip/InHouseTooltip";
import { usePostToAgencyControl } from "../RequestData/hooks/usePostToAgencyControl";
import MetaChip from "../UIToolkit/Badges/MetaChip";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";
import { MultiAgencyPostControl } from "./MultiAgencyPostControl";

/** Match the sibling day-view action icons (call/chat/edit/delete), which render at 16px. */
const TOGGLE_ICON_SX = { fontSize: 16 } as const;

/**
 * How the control presents. All three share the same eligibility logic + confirm dialog
 * (`usePostToAgencyControl`); they differ only in what they render.
 * - `button`   — Requests-tab action area: an upload icon-button, shown only while newly postable.
 * - `chip`     — Requests-tab header: a "Posted to Agency" status chip, shown only once posted.
 * - `iconToggle` — daily-gaps: one icon-button that is clickable (upload) until posted, then flips to a
 *   disabled cloud-done icon. Shown whenever the shift is postable or already posted.
 */
type PostToAgencyVariant = "button" | "chip" | "iconToggle";

type Props = {
	request: ShiftRequest;
	variant: PostToAgencyVariant;
};

export const PostToAgencyControl: FC<Props> = ({ request, variant }) => {
	return !hasLegacyAgencyPosting(request) ? (
		<MultiAgencyPostControl request={request} variant={variant} />
	) : (
		<LegacyPostToAgencyControl request={request} variant={variant} />
	);
};

const LegacyPostToAgencyControl: FC<Props> = ({ request, variant }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const { canNewlyPostToAgency, isPostedToAgency, isPosting, openDialog, dialog } = usePostToAgencyControl(request);
	// Waiting to be sent: the manager already opted in, the agency just hasn't been contacted yet.
	const scheduledDelayHours = (request as OpenShiftRequest).agency_post_delay_hours;
	const scheduledLock =
		scheduledDelayHours != null ? (
			<MetaChip
				tooltip={agencyScheduleDescription(
					// DEFAULT: historical requests can lack source evidence; name the unknown agency rather than guess a vendor.
					("agency_source_name" in request && request.agency_source_name) || t("agency"),
					scheduledDelayHours,
					request.message.shift_start.minus({ hours: scheduledDelayHours }),
					"pending",
					chosenDepartment.timezone,
					DateTime.now(),
					t,
				)}
				tone="warning"
				icon={<LockOutlinedIcon />}
				dataTestId={`request-agency-post-scheduled-${request.id}`}
			/>
		) : null;

	if (variant === "chip") {
		if (scheduledLock) return scheduledLock;
		if (!isPostedToAgency) return null;
		return (
			<MetaChip
				tooltip={t("posted.to.agency")}
				tone="success"
				icon={<CloudDoneOutlinedIcon />}
				dataTestId={`request-posted-to-agency-chip-${request.id}`}
				sx={{ ml: 1 }}
			/>
		);
	}

	const handleClick = (event: MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		openDialog();
	};

	if (variant === "button") {
		if (!canNewlyPostToAgency) return null;
		return (
			<Fragment>
				<SystemButton
					data-testid={`request-post-to-agency-${request.id}`}
					leadingIcon={<CloudUploadOutlinedIcon fontSize={"small"} />}
					tooltip={t("post.to.agency")}
					aria-label={t("post.to.agency")}
					iconButton
					onClick={handleClick}
				/>
				{dialog}
			</Fragment>
		);
	}

	// iconToggle: a compact MUI IconButton sized to match the sibling day-view action icons (SystemButton
	// is too large for that dense cluster). Render only when the shift is agency-relevant (postable now,
	// or already posted).
	if (scheduledLock) return scheduledLock;
	if (!canNewlyPostToAgency && !isPostedToAgency) return null;
	const toggleLabel = isPostedToAgency ? t("posted.to.agency") : t("post.to.agency");
	return (
		<Fragment>
			<InHouseTooltip title={toggleLabel}>
				{/* span wrapper keeps the tooltip working while the button is disabled (posted state). */}
				<span style={{ display: "inline-flex" }}>
					<IconButton
						size="small"
						disabled={isPostedToAgency || isPosting}
						onClick={isPostedToAgency ? undefined : handleClick}
						aria-label={toggleLabel}
						data-testid={`request-post-to-agency-toggle-${request.id}`}
					>
						{isPostedToAgency ? <CloudDoneOutlinedIcon sx={TOGGLE_ICON_SX} /> : <CloudUploadOutlinedIcon sx={TOGGLE_ICON_SX} />}
					</IconButton>
				</span>
			</InHouseTooltip>
			{dialog}
		</Fragment>
	);
};

export default PostToAgencyControl;
